import { useEffect } from 'react';

/**
 * Enhanced Smart TV Spatial D-Pad Navigation Engine
 * Optimized for TV remotes, set-top boxes, and keyboard arrow navigation.
 */

// Cache de curta duração pra evitar recalcular a lista de elementos focáveis (e o escopo do
// modal ativo) a cada tecla, quando o usuário pressiona setas em sequência rápida (ex: segurando
// pra rolar uma grade grande) — cada recálculo força o navegador a recalcular layout de toda a
// tela (getBoundingClientRect/getComputedStyle são caras), o que pesa bastante em hardware fraco.
// Não muda nenhuma lógica de detecção: só reaproveita o resultado se pedido de novo dentro da
// janela de tempo, e recalcula do zero (sempre correto) fora dela ou se a tela mudou.
const FOCUS_CACHE_TTL_MS = 120;
let focusCache: { elements: HTMLElement[]; scopeRoot: HTMLElement | null; timestamp: number } | null = null;
let modalScopeCache: { modal: HTMLElement | null; timestamp: number } | null = null;

export function invalidateFocusCache(): void {
  focusCache = null;
  modalScopeCache = null;
}

// É um campo de texto onde digitar abriria o teclado virtual do Android?
function isTypeableInput(el: HTMLElement): boolean {
  if (el.tagName === 'TEXTAREA') return true;
  if (el.tagName !== 'INPUT') return false;
  const type = (el as HTMLInputElement).type;
  return ['text', 'search', 'email', 'tel', 'url', 'password', 'number'].includes(type);
}

// Só o Player Stick / TV precisa do "campo travado até dar OK" (controle remoto, sem toque). No Player
// Web (celular, tablet, PC) o comportamento dos campos continua exatamente o de sempre. Decidido uma
// única vez, no primeiro uso, pra não mudar no meio da sessão. O modo fica SALVO no aparelho quando a
// URL traz "?mode=stick": o ErrorBoundary recarrega a página sem a query depois de uma queda (comum
// em aparelho de 1 GB), e sem isso a trava de campo pararia de valer. "?mode=web" apaga o valor salvo.
const TV_MODE_STORAGE_KEY = 'opentv_stick_mode';
let tvModeCache: boolean | null = null;
function isTvMode(): boolean {
  if (tvModeCache !== null) return tvModeCache;
  let result = false;
  try {
    const search = window.location?.search || '';
    const ua = navigator.userAgent || '';
    if (/[?&]mode=web\b/.test(search)) {
      try { localStorage.removeItem(TV_MODE_STORAGE_KEY); } catch {}
    } else if (/[?&]mode=stick\b/.test(search)) {
      try { localStorage.setItem(TV_MODE_STORAGE_KEY, '1'); } catch {}
      result = true;
    } else {
      try { result = localStorage.getItem(TV_MODE_STORAGE_KEY) === '1'; } catch {}
    }
    if (!result) {
      result =
        /\b(AFT[A-Z0-9]*|BRAVIA|SmartTV|CrKey|Tizen|Web0S)\b/.test(ua) ||
        /Android.*\bTV\b/.test(ua);
    }
  } catch {}
  tvModeCache = result;
  return result;
}

// Estado dos campos de texto "gerenciados" pela navegação por seta:
//  - data-tv-nav-managed  : o campo foi alcançado por seta pelo menos uma vez (fica marcado)
//  - data-tv-nav-readonly : o campo está travado agora (foco visual, sem teclado)
// Só campos marcados como gerenciados são travados de novo ao perder o foco — campo que nunca
// passou pela navegação por seta (ex: login usado por toque/mouse) não é tocado.
const NAV_MANAGED_ATTR = 'data-tv-nav-managed';
const NAV_LOCKED_ATTR = 'data-tv-nav-readonly';
let activatingInput = false;

function lockInput(input: HTMLInputElement | HTMLTextAreaElement): void {
  input.setAttribute(NAV_MANAGED_ATTR, 'true');
  input.setAttribute(NAV_LOCKED_ATTR, 'true');
  input.readOnly = true;
}

function unlockInput(input: HTMLInputElement | HTMLTextAreaElement): void {
  input.removeAttribute(NAV_LOCKED_ATTR);
  input.readOnly = false;
}

// Foca o elemento com segurança: no Player Stick, se for um campo de texto, deixa travado (somente
// leitura) ANTES de focar, pra navegar por cima dele com a seta não abrir o teclado virtual do
// Android sozinho — o teclado só abre quando o usuário confirma com OK/Enter (ver handler de Enter
// mais abaixo). Pra qualquer outro elemento, e em qualquer outra plataforma, comportamento normal.
function focusSafely(el: HTMLElement): void {
  if (isTvMode() && isTypeableInput(el)) {
    const input = el as HTMLInputElement | HTMLTextAreaElement;
    const managed = input.getAttribute(NAV_MANAGED_ATTR) === 'true';
    // Campo que já é somente-leitura por natureza (e nunca foi gerenciado por nós) não é mexido.
    if (managed || !input.readOnly) {
      lockInput(input);
    }
  }
  el.focus();
}

// Find the currently active modal, dialog, or dropdown menu container, if any
function getActiveModalScope(): HTMLElement | null {
  const now = Date.now();
  if (modalScopeCache && now - modalScopeCache.timestamp < FOCUS_CACHE_TTL_MS) {
    return modalScopeCache.modal;
  }

  const modals = Array.from(
    document.querySelectorAll<HTMLElement>(
      '[role="dialog"], [role="menu"], [aria-modal="true"], [data-modal="true"], [data-dropdown="true"]'
    )
  ).filter((el) => {
    const rect = el.getBoundingClientRect();
    const style = window.getComputedStyle(el);
    return (
      rect.width > 0 &&
      rect.height > 0 &&
      style.display !== 'none' &&
      style.visibility !== 'hidden' &&
      style.opacity !== '0'
    );
  });

  const result = modals.length > 0 ? modals[modals.length - 1] : null;
  modalScopeCache = { modal: result, timestamp: now };
  return result;
}

// Get all interactive and focusable elements within scope
export function getFocusableElements(scopeRoot?: HTMLElement | null): HTMLElement[] {
  const root = scopeRoot || getActiveModalScope() || document.body;

  const now = Date.now();
  if (focusCache && focusCache.scopeRoot === root && now - focusCache.timestamp < FOCUS_CACHE_TTL_MS) {
    return focusCache.elements;
  }

  const focusableSelectors = [
    'button:not([disabled])',
    'a[href]',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
    '[role="button"]:not([disabled])',
  ].join(', ');

  const elements = Array.from(root.querySelectorAll<HTMLElement>(focusableSelectors));

  const filtered = elements.filter((el) => {
    // Check if element is disabled or hidden
    if (el.hasAttribute('disabled') || el.getAttribute('aria-hidden') === 'true') {
      return false;
    }
    const rect = el.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return false;

    // Check if visible on screen
    const style = window.getComputedStyle(el);
    if (style.display === 'none' || style.visibility === 'hidden' || style.opacity === '0') {
      return false;
    }

    // Filter out pointer-events-none containers (like toast wrappers)
    if (style.pointerEvents === 'none') {
      return false;
    }

    return true;
  });

  focusCache = { elements: filtered, scopeRoot: root, timestamp: now };
  return filtered;
}

export function navigateFocus(
  direction: 'ArrowUp' | 'ArrowDown' | 'ArrowLeft' | 'ArrowRight' | 'Up' | 'Down' | 'Left' | 'Right'
): boolean {
  // Wake up TV top navbar and video player controls on any directional interaction
  try {
    window.dispatchEvent(new CustomEvent('tv-activity', { detail: { direction } }));
  } catch {}

  const normalizedDirection: 'ArrowUp' | 'ArrowDown' | 'ArrowLeft' | 'ArrowRight' =
    direction === 'Up' ? 'ArrowUp' :
    direction === 'Down' ? 'ArrowDown' :
    direction === 'Left' ? 'ArrowLeft' :
    direction === 'Right' ? 'ArrowRight' : direction;

  const activeEl = document.activeElement as HTMLElement;
  const isTextInput = activeEl && ['INPUT', 'TEXTAREA'].includes(activeEl.tagName);

  // If typing inside an input or operating range slider, handle Left/Right without shifting focus
  if (isTextInput && (normalizedDirection === 'ArrowLeft' || normalizedDirection === 'ArrowRight')) {
    const input = activeEl as HTMLInputElement;

    if (input.type === 'range') {
      const step = parseFloat(input.step) || 1;
      const min = parseFloat(input.min) || 0;
      const max = parseFloat(input.max) || 100;
      const currentVal = parseFloat(input.value) || 0;

      const seekStep = max > 60 ? 10 : step > 1 ? step : 5;

      let newVal = currentVal;
      if (normalizedDirection === 'ArrowLeft') {
        newVal = Math.max(min, currentVal - seekStep);
      } else if (normalizedDirection === 'ArrowRight') {
        newVal = Math.min(max, currentVal + seekStep);
      }

      const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
        window.HTMLInputElement.prototype,
        'value'
      )?.set;
      if (nativeInputValueSetter) {
        nativeInputValueSetter.call(input, newVal.toString());
      } else {
        input.value = newVal.toString();
      }

      input.dispatchEvent(new Event('input', { bubbles: true }));
      input.dispatchEvent(new Event('change', { bubbles: true }));

      return true;
    }

    if (input.selectionStart !== null && input.selectionEnd !== null) {
      if (normalizedDirection === 'ArrowLeft' && input.selectionStart > 0) return false;
      if (normalizedDirection === 'ArrowRight' && input.selectionEnd < (input.value?.length || 0)) return false;
    }
  }

  const activeModal = getActiveModalScope();
  const focusables = getFocusableElements(activeModal);

  if (focusables.length === 0) return false;

  // If no element or body currently focused, focus the best initial candidate based on direction
  if (!activeEl || activeEl === document.body || !focusables.includes(activeEl)) {
    let candidate: HTMLElement | undefined;

    if (normalizedDirection === 'ArrowUp') {
      candidate = [...focusables].sort((a, b) => a.getBoundingClientRect().top - b.getBoundingClientRect().top)[0];
    } else if (normalizedDirection === 'ArrowDown') {
      candidate = [...focusables].sort((a, b) => b.getBoundingClientRect().bottom - a.getBoundingClientRect().bottom)[0];
    } else {
      candidate =
        focusables.find((el) => el.getAttribute('aria-selected') === 'true' || el.classList.contains('bg-theme-accent') || el.classList.contains('bg-cyan-500') || el.classList.contains('bg-amber-500')) ||
        focusables[0];
    }

    if (candidate) {
      focusSafely(candidate);
      candidate.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'auto' });
      return true;
    }
    return false;
  }

  const currentRect = activeEl.getBoundingClientRect();
  const currentCenterX = currentRect.left + currentRect.width / 2;
  const currentCenterY = currentRect.top + currentRect.height / 2;

  let bestCandidate: HTMLElement | null = null;
  let minScore = Infinity;

  for (const el of focusables) {
    if (el === activeEl) continue;
    if (activeEl.contains(el) || el.contains(activeEl)) continue;

    const rect = el.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;

    let isValidDirection = false;
    let dPrimary = 0;
    let dSecondary = 0;
    let isAligned = false;
    let overlap = 0;

    if (normalizedDirection === 'ArrowRight') {
      if (rect.right > currentRect.right - 2 && rect.left >= currentRect.left + 5) {
        isValidDirection = true;
        dPrimary = rect.left >= currentRect.right ? (rect.left - currentRect.right) : Math.max(0, rect.left - currentRect.left);
        isAligned = rect.top < currentRect.bottom - 4 && rect.bottom > currentRect.top + 4;
        if (isAligned) {
          overlap = Math.min(currentRect.bottom, rect.bottom) - Math.max(currentRect.top, rect.top);
          dSecondary = 0;
        } else {
          dSecondary = rect.bottom <= currentRect.top ? (currentRect.top - rect.bottom) : (rect.top - currentRect.bottom);
        }
      }
    } else if (normalizedDirection === 'ArrowLeft') {
      if (rect.left < currentRect.left + 2 && rect.right <= currentRect.right - 5) {
        isValidDirection = true;
        dPrimary = rect.right <= currentRect.left ? (currentRect.left - rect.right) : Math.max(0, currentRect.right - rect.right);
        isAligned = rect.top < currentRect.bottom - 4 && rect.bottom > currentRect.top + 4;
        if (isAligned) {
          overlap = Math.min(currentRect.bottom, rect.bottom) - Math.max(currentRect.top, rect.top);
          dSecondary = 0;
        } else {
          dSecondary = rect.bottom <= currentRect.top ? (currentRect.top - rect.bottom) : (rect.top - currentRect.bottom);
        }
      }
    } else if (normalizedDirection === 'ArrowDown') {
      if (rect.bottom > currentRect.bottom - 2 && rect.top >= currentRect.top + 5) {
        isValidDirection = true;
        dPrimary = rect.top >= currentRect.bottom ? (rect.top - currentRect.bottom) : Math.max(0, rect.top - currentRect.top);
        isAligned = rect.left < currentRect.right - 4 && rect.right > currentRect.left + 4;
        if (isAligned) {
          overlap = Math.min(currentRect.right, rect.right) - Math.max(currentRect.left, rect.left);
          dSecondary = 0;
        } else {
          dSecondary = rect.right <= currentRect.left ? (currentRect.left - rect.right) : (rect.left - currentRect.right);
        }
      }
    } else if (normalizedDirection === 'ArrowUp') {
      if (rect.top < currentRect.top + 2 && rect.bottom <= currentRect.bottom - 5) {
        isValidDirection = true;
        dPrimary = rect.bottom <= currentRect.top ? (currentRect.top - rect.bottom) : Math.max(0, currentRect.bottom - rect.bottom);
        isAligned = rect.left < currentRect.right - 4 && rect.right > currentRect.left + 4;
        if (isAligned) {
          overlap = Math.min(currentRect.right, rect.right) - Math.max(currentRect.left, rect.left);
          dSecondary = 0;
        } else {
          dSecondary = rect.right <= currentRect.left ? (currentRect.left - rect.right) : (rect.left - currentRect.right);
        }
      }
    }

    if (isValidDirection) {
      let score = 0;
      if (isAligned) {
        const normDimension = (normalizedDirection === 'ArrowUp' || normalizedDirection === 'ArrowDown') ? (currentRect.width || 1) : (currentRect.height || 1);
        score = dPrimary * 1.0 - (overlap / normDimension) * 10;
      } else {
        score = 1000 + dPrimary * 1.5 + dSecondary * 3.0;
      }

      if (score < minScore) {
        minScore = score;
        bestCandidate = el;
      }
    }
  }

  // Natural Transition Engine (Transição suave entre linhas sem loops circulares bruscos)
  if (!bestCandidate && focusables.length > 1) {
    const otherFocusables = focusables.filter((el) => el !== activeEl && !activeEl.contains(el));

    if (normalizedDirection === 'ArrowRight') {
      // Chegou no fim da linha à direita -> Desce naturalmente para a linha seguinte mais próxima abaixo
      const belowItems = otherFocusables.filter((el) => el.getBoundingClientRect().top >= currentRect.bottom - 4);
      if (belowItems.length > 0) {
        bestCandidate = belowItems.sort((a, b) => {
          const ra = a.getBoundingClientRect();
          const rb = b.getBoundingClientRect();
          if (Math.abs(ra.top - rb.top) < 15) return ra.left - rb.left;
          return ra.top - rb.top;
        })[0];
      }
    } else if (normalizedDirection === 'ArrowLeft') {
      // Chegou no início da linha à esquerda -> Sobe naturalmente para o final da linha anterior acima
      const aboveItems = otherFocusables.filter((el) => el.getBoundingClientRect().bottom <= currentRect.top + 4);
      if (aboveItems.length > 0) {
        bestCandidate = aboveItems.sort((a, b) => {
          const ra = a.getBoundingClientRect();
          const rb = b.getBoundingClientRect();
          if (Math.abs(rb.bottom - ra.bottom) < 15) return rb.right - ra.right;
          return rb.bottom - ra.bottom;
        })[0];
      }
    } else if (normalizedDirection === 'ArrowDown') {
      // Chegou no fim da lista abaixo -> Mantém no último item sem dar salto para o topo
      bestCandidate = null;
    } else if (normalizedDirection === 'ArrowUp') {
      // Chegou no topo -> Foca o primeiro item do topo (Navbar ou botão Voltar) se já não estiver nele
      const topItems = [...otherFocusables].filter((el) => el.getBoundingClientRect().top < currentRect.top);
      if (topItems.length > 0) {
        bestCandidate = topItems.sort((a, b) => a.getBoundingClientRect().top - b.getBoundingClientRect().top)[0];
      }
    }
  }

  if (bestCandidate) {
    focusSafely(bestCandidate);
    bestCandidate.scrollIntoView({
      block: 'nearest',
      inline: 'nearest',
      behavior: 'auto',
    });
    return true;
  }

  return false;
}

export function useTVNavigation(enabled = true) {
  useEffect(() => {
    if (!enabled) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      // 1. Arrow Directional Navigation
      const arrowKeys = ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Up', 'Down', 'Left', 'Right'];
      if (arrowKeys.includes(e.key)) {
        const handled = navigateFocus(e.key as any);
        if (handled) {
          e.preventDefault();
        }
        return;
      }

      // 2. Center OK / Enter Action on Active Element
      if (e.key === 'Enter' || e.key === 'Select' || e.key === 'Ok' || e.keyCode === 13) {
        const activeEl = document.activeElement as HTMLElement;
        if (activeEl && activeEl !== document.body) {
          // Campo de texto travado pela navegação por seta — agora sim libera a digitação e abre o
          // teclado, porque o usuário confirmou de propósito com OK/Enter. blur() → liberar →
          // focus() faz o foco "chegar" de novo já como campo editável (dentro do gesto do usuário),
          // que é o que faz o teclado do Android aparecer; activatingInput evita que o focusout
          // desse blur() trave o campo de volta.
          if (activeEl.getAttribute(NAV_LOCKED_ATTR) === 'true') {
            e.preventDefault();
            const input = activeEl as HTMLInputElement | HTMLTextAreaElement;
            activatingInput = true;
            try {
              input.blur();
              unlockInput(input);
              input.focus();
              try {
                const end = input.value.length;
                input.setSelectionRange(end, end);
              } catch {}
            } finally {
              activatingInput = false;
            }
            return;
          }
          // If it's a div/span with role="button" or tabIndex, ensure click is triggered
          if (!['BUTTON', 'INPUT', 'TEXTAREA', 'A'].includes(activeEl.tagName)) {
            e.preventDefault();
            activeEl.click();
          }
        }
      }
    };

    // Quando um campo que a navegação por seta gerencia perde o foco, volta a ficar travado — assim,
    // da próxima vez que a seta passar por cima dele, não abre o teclado sozinho. Campos que nunca
    // passaram pela navegação por seta (não marcados) NÃO são tocados.
    const handleFocusOut = (e: FocusEvent) => {
      if (activatingInput) return;
      const target = e.target as HTMLElement | null;
      if (!target || !isTypeableInput(target)) return;
      if (target.getAttribute(NAV_MANAGED_ATTR) !== 'true') return;
      lockInput(target as HTMLInputElement | HTMLTextAreaElement);
    };

    // Toque ou mouse (ex: mouse aéreo do controle) direto num campo travado libera a digitação na
    // hora — sem isso o campo ficaria somente-leitura pra sempre depois da primeira visita por seta.
    const handlePointerDown = (e: Event) => {
      const target = e.target as HTMLElement | null;
      if (!target || !isTypeableInput(target)) return;
      if (target.getAttribute(NAV_LOCKED_ATTR) === 'true') {
        unlockInput(target as HTMLInputElement | HTMLTextAreaElement);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('focusout', handleFocusOut);
    window.addEventListener('pointerdown', handlePointerDown, true);
    window.addEventListener('mousedown', handlePointerDown, true);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('focusout', handleFocusOut);
      window.removeEventListener('pointerdown', handlePointerDown, true);
      window.removeEventListener('mousedown', handlePointerDown, true);
    };
  }, [enabled]);
}
